import * as THREE from "three";

/**
 * Laboratory 2 · Perspective — the perspective-as-interface scene.
 *
 * Question this scene exists to answer:
 *   Can relationships emerge solely through changes in perspective?
 *
 * Contains three visually identical points of light in an infinite
 * dark space. The three points never change: their color, opacity,
 * scale, and emissive intensity are constant. Only viewpoint changes.
 *
 * Governed by:
 *   /app/memory/SPATIAL_SEMANTICS.md
 *   /app/memory/MATERIAL_SPECIFICATION.md   (Law Seven — darkness)
 */


// -- Cognitive-layer defaults ---------------------------------------------
//
// These are research variables, not engine variables. The
// implementation translates them into camera / object positions
// internally.

export const DEFAULT_PARAMS = {
  // How much of the perspective journey has been traveled.
  // 0 = observer sees only one thing.
  // 1 = observer sees the entire arrangement.
  perspectiveScale: 0.0,

  // Whether perspectiveScale animates on its own (autopilot for
  // hands-off observation).
  autoAnimate: false,

  // Global scale of the arrangement in space.
  objectSeparation: 1.0,

  // Where along the perspective journey each additional object first
  // becomes discoverable. These bias the camera easing.
  discoveryThreshold2: 0.30,
  discoveryThreshold3: 0.65,

  // How far out the observer can withdraw.
  contextRadius: 6.0,

  // Which agent is doing the moving.
  //   "camera-moves" — the observer withdraws; the world is still.
  //   "world-moves"  — the observer is still; the world reorganizes.
  mode: "camera-moves",
};


// -- The scene ------------------------------------------------------------

export function createPerspectiveScene({ container, initialParams }) {
  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || window.innerHeight;

  const scene = new THREE.Scene();
  // Darkness is never black. Law Seven, honored here too.
  scene.background = new THREE.Color(0x040406);

  const camera = new THREE.PerspectiveCamera(32, width / height, 0.01, 200);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  // Faint hemisphere fill so darkness renders as space, not absence.
  const hemi = new THREE.HemisphereLight(0x151520, 0x000000, 0.06);
  scene.add(hemi);

  // -- Three points of light -------------------------------------------
  //
  // Deliberately identical. No color, size, or opacity difference
  // between them. Meaning must come entirely from relationship, not
  // from any per-object property.

  // Base (rest) positions of the three objects, normalized around the
  // origin. Object A sits close to the camera path; B and C are
  // pushed far enough sideways that at perspectiveScale = 0 they lie
  // outside the camera frustum entirely. Only when the observer
  // withdraws do B and C enter view.
  const BASE_POSITIONS = [
    new THREE.Vector3( 0.00,  0.00, -0.35),  // A — near
    new THREE.Vector3( 1.75,  0.42, -1.35),  // B — far right
    new THREE.Vector3(-1.85, -0.55, -1.55),  // C — far left, lower
  ];

  const sourceGeo = new THREE.SphereGeometry(0.12, 32, 32);
  const objectMaterial = new THREE.MeshBasicMaterial({
    color: 0xf3ead6, // warm off-white; identical across all three
    toneMapped: false,
  });
  const objects = BASE_POSITIONS.map((p) => {
    const mesh = new THREE.Mesh(sourceGeo, objectMaterial);
    mesh.position.copy(p);
    scene.add(mesh);
    return mesh;
  });

  // A very subtle glow ring around each — implemented once, not
  // animated. Establishes them as "points of light" rather than
  // "spheres of matter." Same across all three.
  const glowGeo = new THREE.SphereGeometry(0.32, 24, 24);
  const glowMat = new THREE.MeshBasicMaterial({
    color: 0xd9b98a,
    transparent: true,
    opacity: 0.05,
    depthWrite: false,
    toneMapped: false,
  });
  const glows = BASE_POSITIONS.map((p) => {
    const g = new THREE.Mesh(glowGeo, glowMat);
    g.position.copy(p);
    scene.add(g);
    return g;
  });


  let currentParams = { ...initialParams };


  // -- Cognitive → engine translation -----------------------------------
  //
  // The panel exposes research variables. Everything below translates
  // them into concrete camera and object positions.

  // A smooth easing that lets the observer "pause" a moment near each
  // discovery threshold — so revelation feels earned, not automatic.
  function easedPath(t, t2, t3) {
    // Piecewise easing across three segments: [0..t2], [t2..t3], [t3..1].
    // Each segment gets a smoothstep so entering and leaving them is
    // never sharp.
    function smoothstep(x) {
      x = Math.max(0, Math.min(1, x));
      return x * x * (3 - 2 * x);
    }
    // We interpret t2 and t3 as the perspective-scale checkpoints at
    // which objects B and C should be reaching their "settled" frame
    // positions. The camera continues to pull back linearly between
    // checkpoints, but easing softens the arrival.
    return smoothstep(t); // simplest: single smoothstep over the whole [0..1]
    // t2, t3 are informational for the researcher and used elsewhere.
  }

  function updateCamera(p) {
    const t = easedPath(p.perspectiveScale, p.discoveryThreshold2, p.discoveryThreshold3);
    // Camera path: from a comfortable framing of object A, straight
    // back along +Z. zNear is chosen so A does not exceed the
    // vertical FOV at perspectiveScale = 0.
    const zNear = 0.55;
    const zFar = p.contextRadius;
    const z = zNear + (zFar - zNear) * t;
    camera.position.set(0, 0, z);
    camera.lookAt(0, 0, -1);
  }

  function updateObjects(p) {
    if (p.mode === "camera-moves") {
      // World is still. Objects sit at their base positions, scaled
      // by objectSeparation.
      objects.forEach((mesh, i) => {
        const base = BASE_POSITIONS[i];
        mesh.position.set(
          base.x * p.objectSeparation,
          base.y * p.objectSeparation,
          base.z * p.objectSeparation,
        );
        glows[i].position.copy(mesh.position);
      });
    } else {
      // "world-moves" — the observer is still and the arrangement
      // reorganizes. At perspectiveScale = 0, the three objects
      // overlap near object A. At perspectiveScale = 1, they occupy
      // their base positions × objectSeparation.
      const t = easedPath(
        p.perspectiveScale,
        p.discoveryThreshold2,
        p.discoveryThreshold3,
      );
      objects.forEach((mesh, i) => {
        const base = BASE_POSITIONS[i];
        // Objects 2 and 3 start at object A's base position and slide
        // to their own base positions, each along their own threshold.
        let localT;
        if (i === 0) {
          localT = 1;
        } else {
          const threshold =
            i === 1 ? p.discoveryThreshold2 : p.discoveryThreshold3;
          // A soft ramp starting at (threshold - 0.1) and finishing
          // at min(1, threshold + 0.35). Objects arrive quietly.
          const start = Math.max(0, threshold - 0.1);
          const end = Math.min(1, threshold + 0.35);
          localT = Math.max(0, Math.min(1, (p.perspectiveScale - start) / (end - start)));
          localT = localT * localT * (3 - 2 * localT);
        }
        const startPos = BASE_POSITIONS[0].clone().multiplyScalar(p.objectSeparation);
        const endPos = base.clone().multiplyScalar(p.objectSeparation);
        mesh.position.lerpVectors(startPos, endPos, localT);
        glows[i].position.copy(mesh.position);
      });
    }
  }

  function updateCameraForMode(p) {
    if (p.mode === "camera-moves") {
      updateCamera(p);
    } else {
      // "world-moves" — camera stays close, matching the near-framing
      // position of updateCamera(scale=0).
      camera.position.set(0, 0, 0.55);
      camera.lookAt(0, 0, -1);
    }
  }

  function apply(p) {
    updateObjects(p);
    updateCameraForMode(p);
  }
  apply(currentParams);


  // -- Relationship visibility (computed readout) -----------------------
  //
  // How many objects are currently inside the camera frustum, and how
  // spread they are across the frame. Returned each frame for the
  // debug panel to display.

  const _frustum = new THREE.Frustum();
  const _projScreenMatrix = new THREE.Matrix4();
  function computeRelationshipVisibility() {
    _projScreenMatrix.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse,
    );
    _frustum.setFromProjectionMatrix(_projScreenMatrix);

    let visible = 0;
    const inFrame = objects.map((mesh) => {
      const ok = _frustum.containsPoint(mesh.position);
      if (ok) visible++;
      return ok;
    });
    if (visible < 2) return { value: visible / 3, visible, inFrame };

    // Spread: max pairwise angular distance in NDC space.
    const ndcs = objects
      .filter((_, i) => inFrame[i])
      .map((mesh) => {
        const v = mesh.position.clone().project(camera);
        return new THREE.Vector2(v.x, v.y);
      });
    let maxSpread = 0;
    for (let i = 0; i < ndcs.length; i++) {
      for (let j = i + 1; j < ndcs.length; j++) {
        const d = ndcs[i].distanceTo(ndcs[j]);
        if (d > maxSpread) maxSpread = d;
      }
    }
    // Normalize: two visible points at max spread of ~sqrt(8) in NDC
    // (corner-to-corner). Cap at 1.5 as a reasonable practical max.
    const spreadFactor = Math.min(1, maxSpread / 1.4);
    // Blend: presence and spread together indicate relationship.
    const value = (visible / 3) * (0.5 + 0.5 * spreadFactor);
    return { value, visible, inFrame };
  }


  // -- Animation loop ---------------------------------------------------

  const clock = new THREE.Clock();
  let running = true;
  let autoTime = 0;
  let onFrame = null;

  function loop() {
    if (!running) return;
    requestAnimationFrame(loop);
    const dt = clock.getDelta();

    if (currentParams.autoAnimate) {
      // Slow triangle wave from 0 → 1 → 0 over about 40 seconds.
      autoTime += dt / 20;
      const phase = autoTime % 2;
      const eased = phase <= 1 ? phase : 2 - phase;
      currentParams = { ...currentParams, perspectiveScale: eased };
      apply(currentParams);
    }

    const rv = computeRelationshipVisibility();
    if (onFrame) onFrame({ relationshipVisibility: rv, params: currentParams });

    renderer.render(scene, camera);
  }
  loop();


  // -- Resize -----------------------------------------------------------

  function onResize() {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }
  window.addEventListener("resize", onResize);


  // -- Screenshot -------------------------------------------------------

  function screenshot({ presetName = "unsaved", scale = 2 } = {}) {
    const oldSize = renderer.getSize(new THREE.Vector2());
    const w = Math.round(oldSize.x * scale);
    const h = Math.round(oldSize.y * scale);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);

    const dataUrl = renderer.domElement.toDataURL("image/png");

    renderer.setSize(oldSize.x, oldSize.y, false);
    camera.aspect = oldSize.x / oldSize.y;
    camera.updateProjectionMatrix();

    const timestamp = new Date().toISOString();
    const meta = {
      lab: "perspective",
      timestamp,
      preset: presetName,
      cameraPosition: camera.position.toArray(),
      cameraOrientation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
      params: { ...currentParams },
    };

    const stamp = timestamp.replace(/[:.]/g, "-");
    const pngLink = document.createElement("a");
    pngLink.href = dataUrl;
    pngLink.download = `perspective_${stamp}.png`;
    document.body.appendChild(pngLink);
    pngLink.click();
    document.body.removeChild(pngLink);

    const blob = new Blob([JSON.stringify(meta, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const jsonLink = document.createElement("a");
    jsonLink.href = url;
    jsonLink.download = `perspective_${stamp}.json`;
    document.body.appendChild(jsonLink);
    jsonLink.click();
    document.body.removeChild(jsonLink);
    URL.revokeObjectURL(url);
  }


  // -- Dispose ---------------------------------------------------------

  function dispose() {
    running = false;
    window.removeEventListener("resize", onResize);
    if (renderer.domElement.parentNode === container) {
      container.removeChild(renderer.domElement);
    }
    sourceGeo.dispose();
    glowGeo.dispose();
    objectMaterial.dispose();
    glowMat.dispose();
    renderer.dispose();
  }


  return {
    setParams(p) {
      currentParams = { ...p };
      apply(currentParams);
    },
    setOnFrame(fn) {
      onFrame = fn;
    },
    screenshot,
    dispose,
  };
}
